// FAME-toned intro copy generator for /services/:service/:city pages.
// Different voice than FDME: agency / retainer frame, not leak-fix frame.
// Rotates deterministically on hash(city, service) so every URL renders
// identical copy on reload, but neighbors differ in 40%+ of body text.

import type { CityData } from './broward-cities';
import type { ServiceData } from './services';

const OPENING_POOL: Array<(c: CityData, s: ServiceData) => string> = [
  (c, s) =>
    `If you run a business in ${c.name}, Florida, ${s.display_short} isn\'t optional anymore — your buyers are already researching the competition through ChatGPT, Perplexity, and Gemini before they ever open a browser tab.`,
  (c, s) =>
    `${c.name} is a ${c.population.toLocaleString()}-resident market, and the companies winning here aren\'t the ones spending more on ads — they\'re the ones whose ${s.display_short} stack actually talks to itself.`,
  (c, s) =>
    `Every week in ${c.name}, a buyer near ${c.notable_neighborhoods[0]} or ${c.notable_neighborhoods[1]} asks an AI assistant for a recommendation. If your ${s.display_short} isn\'t built for that moment, you never show up on the shortlist.`,
  (c, s) =>
    `Around ${c.attractions.split(',')[0]}, the businesses compounding right now aren\'t the loudest advertisers — they\'re the ones whose ${s.display_short} is architected for AI retrieval, measurable attribution, and a single pane of glass.`,
  (c, s) =>
    `${c.name} — ${c.unique_hook} — has roughly ${Math.floor(c.population / 1000)}K residents and a stack of competitors still treating ${s.display_short} as three disconnected vendors. That gap is what we close.`,
];

const MIDDLE_POOL: Array<(c: CityData, s: ServiceData) => string> = [
  (c, s) =>
    `We\'re an AI-first marketing agency serving ${c.name} and the surrounding ${c.county} County corridor. We run ${s.display_short} as a fully integrated retainer — no handoffs, no finger-pointing, one owner for the whole funnel.`,
  (c, s) =>
    `Our retainer engagement wraps ${s.display_short} into an always-on system: AI readiness, generative search visibility, 24/7 voice capture, and weekly attribution reporting tuned to ${c.name} buyers.`,
  (c, s) =>
    `We plug into your tech stack, assess every ${s.display_short} touchpoint across zip codes ${c.zip_codes.slice(0, 3).join(', ')}, and start shipping installs inside week one. No 90-day discovery.`,
  (c, s) =>
    `Between ${c.notable_neighborhoods[0]}, ${c.notable_neighborhoods[1]}, and ${c.notable_neighborhoods[2]}, your buyers are searching, asking assistants, and scrolling reviews. Our retainer makes sure ${s.display_short} shows up in every one of those motions.`,
];

const PROOF_POOL: Array<(c: CityData, _s: ServiceData) => string> = [
  (c, _s) =>
    `We\'ve run AI visibility scans on businesses across ${c.name}, ${c.nearby[0].replace(/-/g, ' ')}, and ${c.nearby[1].replace(/-/g, ' ')} — and most of them are invisible to ChatGPT and Perplexity right now.`,
  (c, _s) =>
    `We know exactly how Google Business Profile + generative search rank in ${c.name} — down to the zip-code radius that actually drives high-ticket work.`,
  (c, _s) =>
    `We\'ve assessed dozens of ${c.name}-metro brands against their top three competitors and can show you the exact attention gaps, week one.`,
  (c, _s) =>
    `${c.name} brands are already compounding with us. The ones still running three disconnected vendors are the ones getting out-marketed by smaller, smarter competitors.`,
];

function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function generateCityIntro(city: CityData, service: ServiceData): {
  opening: string;
  middle: string;
  proof: string;
} {
  const seed = hash(`${city.slug}-${service.slug}`);
  return {
    opening: OPENING_POOL[seed % OPENING_POOL.length](city, service),
    middle: MIDDLE_POOL[(seed >> 2) % MIDDLE_POOL.length](city, service),
    proof: PROOF_POOL[(seed >> 3) % PROOF_POOL.length](city, service),
  };
}

// Scanned-brands count, 12-40 range, deterministic per city+service.
export function scannedCount(city: CityData, service: ServiceData): number {
  const seed = hash(`${city.slug}:${service.slug}:scanned`);
  return 12 + (seed % 29);
}

// Monthly AI-visibility search estimate (deterministic).
export function monthlySearchEstimate(city: CityData, service: ServiceData): number {
  const base = Math.max(40, Math.floor(city.population / 2000));
  const serviceMultiplier: Record<string, number> = {
    'ai-marketing': 1.1,
    'voice-ai': 1.3,
    'seo-automation': 1.4,
    'chatgpt-ads': 0.8,
    'geo-content': 0.9,
  };
  const m = serviceMultiplier[service.slug] ?? 1;
  return Math.round(base * m);
}
